// src/pages/hr/recruitment/CandidateList.jsx
import React, { useState } from "react";
import { useRecruitmentContext } from "../../../context/RecruitmentContext";
import { createCandidate } from "../../../hooks/useRecruitment";
import { useAuth } from "../../../context/AuthContext";
import CandidateProfile from "./CandidateProfile";

export default function CandidateList() {
  const { candidates, candidatesLoading, jobOpenings, stages, stageColors } = useRecruitmentContext();
  const { userProfile } = useAuth();

  const [search, setSearch] = useState("");
  const [filterStage, setFilterStage] = useState("all");
  const [filterJob, setFilterJob] = useState("all");

  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    appliedPosition: "",
    jobId: "",
    experience: "3 years",
    currentCompany: "",
    currentSalary: "",
    expectedSalary: "",
    noticePeriod: "30 days",
    source: "Direct",
    skills: "React, Node.js",
    aiScore: 85,
    stage: "Applied",
  });

  const filtered = candidates.filter((c) => {
    const matchSearch =
      (c.name || "").toLowerCase().includes(search.toLowerCase()) ||
      (c.email || "").toLowerCase().includes(search.toLowerCase()) ||
      (c.appliedPosition || c.role || "").toLowerCase().includes(search.toLowerCase());
    const matchStage = filterStage === "all" || c.stage === filterStage;
    const matchJob = filterJob === "all" || c.jobId === filterJob;
    return matchSearch && matchStage && matchJob;
  });

  async function handleAddSubmit(e) {
    e.preventDefault();
    const skillsArr = typeof formData.skills === "string"
      ? formData.skills.split(",").map((s) => s.trim()).filter(Boolean)
      : formData.skills;

    await createCandidate({
      ...formData,
      skills: skillsArr,
      aiScore: Number(formData.aiScore),
      createdByName: userProfile?.displayName || "HR Admin",
    });
    setShowAddModal(false);
  }

  function handleExportCSV() {
    const headers = ["Name", "Email", "Phone", "Applied Position", "Stage", "AI Score", "Experience", "Current Company", "Source"];
    const rows = filtered.map((c) => [
      `"${c.name || ""}"`,
      `"${c.email || ""}"`,
      `"${c.phone || ""}"`,
      `"${c.appliedPosition || c.role || ""}"`,
      `"${c.stage || ""}"`,
      c.aiScore || 0,
      `"${c.experience || ""}"`,
      `"${c.currentCompany || ""}"`,
      `"${c.source || ""}"`,
    ]);
    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `candidates_export_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
      <style>{`
        .cand-table { width: 100%; border-collapse: collapse; font-size: 0.84rem; background: var(--bg-card, #fff); border-radius: 12px; overflow: hidden; border: 1px solid var(--border, #e5e7eb); box-shadow: 0 1px 5px rgba(0,0,0,0.05); }
        .cand-table th { background: var(--bg-secondary, #f9fafb); text-align: left; padding: 0.7rem 0.9rem; font-size: 0.74rem; font-weight: 700; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid var(--border, #e5e7eb); }
        .cand-table td { padding: 0.75rem 0.9rem; border-bottom: 1px solid var(--border, #f3f4f6); vertical-align: middle; color: var(--text-primary, #111); }
        .cand-table tr:hover td { background: var(--bg-secondary, #f9fafb); cursor: pointer; }
        .stage-pill { display: inline-block; padding: 0.2rem 0.65rem; border-radius: 99px; font-size: 0.72rem; font-weight: 700; color: #fff; }
        .score-bar-bg { height: 6px; width: 60px; background: #e5e7eb; border-radius: 99px; overflow: hidden; display: inline-block; vertical-align: middle; margin-right: 6px; }
        .score-bar-fill { height: 100%; background: #10b981; border-radius: 99px; }
      `}</style>

      {/* Toolbar */}
      <div style={{ display: "flex", justifyContent: "space-between", gap: "0.75rem", flexWrap: "wrap", alignItems: "center" }}>
        <input
          type="text"
          className="ats-input"
          placeholder="🔍 Search candidate name, position, email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ minWidth: 260 }}
        />
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <select className="ats-select" value={filterStage} onChange={(e) => setFilterStage(e.target.value)}>
            <option value="all">All Stages</option>
            {stages.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select className="ats-select" value={filterJob} onChange={(e) => setFilterJob(e.target.value)}>
            <option value="all">All Jobs</option>
            {jobOpenings.map((j) => (
              <option key={j.id} value={j.id}>{j.title}</option>
            ))}
          </select>
          <button className="btn-secondary" onClick={handleExportCSV}>📥 Export CSV</button>
          <button className="btn-primary" onClick={() => setShowAddModal(true)}>+ Add Candidate</button>
        </div>
      </div>

      {/* Candidates Table */}
      {candidatesLoading ? (
        <div style={{ textAlign: "center", padding: "3rem", color: "var(--text-muted)" }}>Loading Candidates...</div>
      ) : filtered.length === 0 ? (
        <div style={{ background: "var(--bg-card, #fff)", border: "1px dashed var(--border)", borderRadius: 12, padding: "3rem", textAlign: "center", color: "var(--text-muted)" }}>
          No candidates found matching criteria.
        </div>
      ) : (
        <table className="cand-table">
          <thead>
            <tr>
              <th>Candidate</th>
              <th>Applied Position</th>
              <th>Stage</th>
              <th>AI Match</th>
              <th>Experience</th>
              <th>Current Company</th>
              <th>Source</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} onClick={() => setSelectedCandidate(c)}>
                <td>
                  <div style={{ fontWeight: 700, color: "var(--text-primary)" }}>{c.name}</div>
                  <div style={{ fontSize: "0.74rem", color: "var(--text-muted)" }}>{c.email}</div>
                </td>
                <td style={{ fontWeight: 600 }}>{c.appliedPosition || c.role || "—"}</td>
                <td>
                  <span className="stage-pill" style={{ background: stageColors[c.stage] || "#1b4b31" }}>
                    {c.stage || "Applied"}
                  </span>
                </td>
                <td>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div className="score-bar-bg">
                      <div className="score-bar-fill" style={{ width: `${c.aiScore || 0}%` }} />
                    </div>
                    <span style={{ fontWeight: 700, fontSize: "0.78rem", color: "#1b4b31" }}>{c.aiScore || 0}%</span>
                  </div>
                </td>
                <td>{c.experience || "—"}</td>
                <td>{c.currentCompany || "—"}</td>
                <td>
                  <span style={{ background: "var(--bg-secondary)", padding: "0.15rem 0.5rem", borderRadius: 4, fontSize: "0.74rem", color: "var(--text-muted)" }}>
                    {c.source || "Direct"}
                  </span>
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <button className="btn-secondary" style={{ padding: "0.25rem 0.6rem", fontSize: "0.75rem" }} onClick={() => setSelectedCandidate(c)}>
                    View Profile
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Candidate Profile Modal */}
      {selectedCandidate && (
        <CandidateProfile
          candidate={selectedCandidate}
          onClose={() => setSelectedCandidate(null)}
        />
      )}

      {/* Add Candidate Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-body" style={{ maxWidth: 600 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 700 }}>Add Candidate Profile</h3>
              <button className="btn-secondary" style={{ padding: "0.2rem 0.5rem" }} onClick={() => setShowAddModal(false)}>✕</button>
            </div>
            <form onSubmit={handleAddSubmit} style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Full Name *</label>
                  <input type="text" className="ats-input" style={{ width: "100%" }} required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                </div>
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Email Address *</label>
                  <input type="email" className="ats-input" style={{ width: "100%" }} required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                </div>
              </div>
              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Phone Number</label>
                  <input type="text" className="ats-input" style={{ width: "100%" }} value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                </div>
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Applied Position *</label>
                  <input type="text" className="ats-input" style={{ width: "100%" }} required placeholder="e.g. Senior React Dev" value={formData.appliedPosition} onChange={(e) => setFormData({ ...formData, appliedPosition: e.target.value })} />
                </div>
              </div>
              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Experience</label>
                  <input type="text" className="ats-input" style={{ width: "100%" }} placeholder="e.g. 4 years" value={formData.experience} onChange={(e) => setFormData({ ...formData, experience: e.target.value })} />
                </div>
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Current Company</label>
                  <input type="text" className="ats-input" style={{ width: "100%" }} placeholder="e.g. Tech Corp" value={formData.currentCompany} onChange={(e) => setFormData({ ...formData, currentCompany: e.target.value })} />
                </div>
              </div>
              <div className="form-grid-2">
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Source</label>
                  <select className="ats-select" style={{ width: "100%" }} value={formData.source} onChange={(e) => setFormData({ ...formData, source: e.target.value })}>
                    <option value="Direct">Direct Application</option>
                    <option value="Employee Referral">Employee Referral</option>
                    <option value="LinkedIn">LinkedIn</option>
                    <option value="Naukri">Naukri</option>
                    <option value="Indeed">Indeed</option>
                    <option value="Campus">Campus Drive</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: "0.78rem", fontWeight: 600 }}>Initial AI Match Score (%)</label>
                  <input type="number" min="0" max="100" className="ats-input" style={{ width: "100%" }} value={formData.aiScore} onChange={(e) => setFormData({ ...formData, aiScore: e.target.value })} />
                </div>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.5rem" }}>
                <button type="button" className="btn-secondary" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary">Add Candidate</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
